import { ServicePluginDefinition } from '@wix/sdk-types';
import {
  Context,
  GetValidationViolationsRequest,
  GetValidationViolationsResponse,
} from './service-plugins-types.js';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';

export interface GetValidationViolationsEnvelope {
  request: GetValidationViolationsRequest;
  metadata: Context;
}

export const provideHandlers = ServicePluginDefinition<{
  /**
   *
   * This method retrieves validation violations from your app.
   *
   * Wix calls this method when certain actions are performed on a visitor's cart and checkout. For example, when an item is added to the cart, or when a coupon is added to a checkout.
   * This method validates a visitor's cart and checkout, and returns any validation violations (using the structure provided by Wix eCommerce). Site visitors can see the validation violations in their cart and checkout pages. If there aren't any validation violations, the method returns an object containing an empty list.
   *
   * > __Notes:__
   * > + Do not call the Estimate Cart Totals, Estimate Current Cart Totals or Get Checkout methods from your implementation code for Get Validation Violations. Doing so will result in an error.
   * > + By default, this method only retrieves validation violations from a visitor's checkout. If you want to also retrieve validation violations from a visitor's cart, set the `validateInCart` parameter to `true` in the Ecom Validations Integration's config file. */
  getValidationViolations(
    payload: GetValidationViolationsEnvelope
  ): GetValidationViolationsResponse | Promise<GetValidationViolationsResponse>;
}>('ECOM_VALIDATIONS', [
  {
    name: 'getValidationViolations',
    primaryHttpMappingPath: '/v1/get-violations',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = payload;

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'request.validationInfo.lineItems.physicalProperties.weight',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'request.validationInfo.lineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'request.validationInfo.shippingInfo.selectedCarrierServiceOption.deliveryTimeSlot.from',
              },
              {
                path: 'request.validationInfo.shippingInfo.selectedCarrierServiceOption.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'request.validationInfo.billingInfo.address' },
              { path: 'request.validationInfo.shippingAddress.address' },
            ],
          },
        ]);

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },
]);
